import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";
import { getStaticContentHost } from "utils/staticContent/staticContent";

import { ApiClient } from "@/utils/apiClient";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "helpers/awsImageFunctions",
);
const EDITABLE_CONTENT_BLOCK_IMAGE_MAX_BYTES = 1_048_576;
const ADMIN_IMAGE_UPLOAD_URL_ENDPOINT =
  "/admin/editable-content-blocks/images/upload-url";
const ADMIN_IMAGE_DELETE_ENDPOINT =
  "/admin/editable-content-blocks/images/delete";

interface EditableContentBlockImageUploadResponse {
  objectKey: string;
  uploadUrl: string;
}

/**
 * Uploads an image to the public static bucket and returns the stored key.
 *
 * @remarks
 * This is a storage helper used by the admin content-block editor. It throws user-displayable errors for
 * validation failures (wrong file type / size too large) so the caller can decide how to present feedback
 * (snackbar/dialog/etc). The browser never receives long-lived AWS credentials; it requests an admin-only
 * signed upload URL from the backend, then uploads directly to the public static bucket.
 *
 * Public images are stored under `img/<name>` and served from `VITE_STATIC_CONTENT_HOST`.
 *
 * @see docs/guides/STATIC_CONTENT.md
 */
const uploadImageToAws: (
  file: File,
  fileName: string,
) => Promise<string> = async (file, fileName) => {
  if (!file.type.includes("image")) {
    throw new Error("Please upload an image file");
  }

  if (file.size > EDITABLE_CONTENT_BLOCK_IMAGE_MAX_BYTES) {
    throw new Error("Please resize the image to be smaller than 1MB.");
  }

  const extension = file.name.split(".").pop()?.trim();
  const requestedFileName = extension ? `${fileName}.${extension}` : fileName;
  const staticContentHost = getStaticContentHost();
  let objectKey: string | undefined;

  try {
    const uploadResponse = (await ApiClient.post(
      ADMIN_IMAGE_UPLOAD_URL_ENDPOINT,
      {
        fileName: requestedFileName,
        contentType: file.type,
        fileSize: file.size,
      },
    )) as EditableContentBlockImageUploadResponse;

    if (
      typeof uploadResponse.objectKey !== "string" ||
      typeof uploadResponse.uploadUrl !== "string"
    ) {
      throw new Error("Invalid image upload response");
    }

    objectKey = uploadResponse.objectKey;
    const uploadResult = await fetch(uploadResponse.uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
      body: file,
    });
    if (!uploadResult.ok) {
      logger.error("Signed image upload failed", undefined, {
        operation: "editable_content_block_image_upload_failed",
        objectKey: uploadResponse.objectKey,
        staticContentHost,
        status: uploadResult.status,
      });
      throw new Error("Image upload failed. Please try again.");
    }

    return uploadResponse.objectKey;
  } catch (err) {
    logger.error(
      "Failed to upload image to static content storage",
      err instanceof Error ? err : undefined,
      {
        operation: "editable_content_block_image_upload_failed",
        requestedFileName,
        staticContentHost,
        objectKey,
      },
    );
    if (
      err instanceof Error &&
      err.message.trim().length > 0 &&
      err.message !== "Invalid image upload response"
    ) {
      throw err;
    }
    throw new Error("Image upload failed. Please try again.");
  }
};

/**
 * Deletes one admin-managed public content-block image.
 *
 * @remarks
 * Callers may pass either a bare file name or a stored `img/...` object key. Delete failures are logged
 * for follow-up but intentionally not rethrown because the editor should not block content saves when
 * cleanup of an orphaned public image fails.
 */
const deleteFileFromAws = async (objectKey: string): Promise<void> => {
  const staticContentHost = getStaticContentHost();
  const normalisedKey = objectKey.includes("/")
    ? objectKey
    : `img/${objectKey}`;

  try {
    await ApiClient.post(ADMIN_IMAGE_DELETE_ENDPOINT, {
      objectKey: normalisedKey,
    });
  } catch (err) {
    logger.error(
      "Failed to delete image from static content storage",
      err instanceof Error ? err : undefined,
      {
        operation: "editable_content_block_image_delete_failed",
        staticContentHost,
        objectKey: normalisedKey,
      },
    );
  }
};

export default { uploadImageToAws, deleteFileFromAws };
